import { Typography } from "antd"
import { Link } from "gatsby"
import React, { useEffect, useState } from "react"
import { Animated } from "react-animated-css"
import { useRecoilState } from "recoil"
import counterState from "../services/counterAtom"
import UserInfoModal from "./UserInfoModal"

// Rewritten per CONTENT-ARCHITECTURE §0. The message is unchanged — you have
// technology, the value has not arrived, and closing that gap is the work —
// but it is now addressed to the reader rather than describing the company.
// Previous H1, kept here for the 30-day Search Console comparison and revert:
//   "Helping SMEs Harness the Power of AI for Unmatched Growth"
// <title> and meta description are untouched and stay byte-identical.
/**
 * Sentence-final marks for the H1.
 *
 * Each clause ends with an icon instead of a visible full stop, and each icon
 * says what its clause says: a line rising, the same line inverted, and value
 * draining away. Two of the three are deliberately the same mark mirrored —
 * "grew" and "did not" are one comparison, so drawing them as one shape read
 * both ways carries the sentence better than two unrelated pictures would.
 *
 * The third is a coin stack with an outflow arrow, NOT a currency glyph. A
 * currency symbol is prohibited on this page (LANDING-PLAN), and that applies
 * to a symbol drawn as artwork exactly as it applies to one typed as text.
 *
 * Stroke, viewBox and cap/join match the icon set already in S1
 * (StartWhereYouAre.js), so these are the same family and not a second one.
 *
 * aria-hidden throughout: the icons are punctuation, and the stop they replace
 * is still in the DOM. See .title__stop.
 */
const ICON_PROPS = {
  className: "title__icon",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
  focusable: "false",
}

// "The business grew" — a trend line climbing to the top right.
const IconGrowth = () => (
  <svg {...ICON_PROPS}>
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
)

// "The software did not" — the same trend line, mirrored, falling away.
const IconDecline = () => (
  <svg {...ICON_PROPS}>
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
    <polyline points="17 18 23 18 23 12" />
  </svg>
)

// "Your margin pays for it" — a coin, and the arrow taking it away.
//
// Two shapes, because that is the budget. This mark renders at 16px on a
// phone, where the two above survive only by being two polylines each. A coin
// stack was tried twice — side by side with the arrow, then above it — and
// both times the extra strokes turned to mud at that size, the second reading
// as a database cylinder besides.
//
// It also keeps all three marks in the same language: every clause ends in an
// arrow, and this one has what is being lost attached to it.
const IconOutflow = () => (
  <svg {...ICON_PROPS}>
    <circle cx="8" cy="10" r="6.5" />
    <line x1="19.5" y1="5.5" x2="19.5" y2="20.5" />
    <polyline points="16.5 17.5 19.5 20.5 22.5 17.5" />
  </svg>
)

// Split only for the marks. The words are the words in HEADLINE below, and
// the rendered text content of the H1 still resolves to that string exactly.
const HEADLINE = [
  { text: "The business grew", Icon: IconGrowth },
  { text: "The software did not", Icon: IconDecline },
  { text: "Your margin pays for it", Icon: IconOutflow },
]

// Names the four capability areas (Odoo implementation, Dynamics 365
// optimisation, AI-native platforms, tailor-made web and mobile) and the
// same-team-throughout commitment.
//
// The team promise is back in the hero, having been cut from it earlier and
// left to S4. It is now stated in both places, which is deliberate.
const subTextArray = [
  [
    "We close that gap with software built around how you actually work — whether that means implementing Odoo, optimising your Dynamics 365, building an AI-native platform, or developing tailor-made web and mobile software. Whichever it is, the same team stays with you from first call to final handover."
  ],
]

/**
 * Cumulative development hours, reinstated. The counter and its constants are
 * carried over unchanged from the stat band this replaced: a baseline of
 * 230,400 at 2026-05-05, accruing 160 per day.
 *
 * Formatted with an explicit separator rather than toLocaleString(), which
 * resolves against the host locale — that produced one string when Gatsby
 * rendered the HTML at build time and potentially another in the browser,
 * which is a hydration mismatch.
 */
const developmentHours = () => {
  const days = Math.floor((Date.now() - new Date("2026-05-05").getTime()) / 86400000)
  const hours = 230400 + days * 160
  // Abbreviated, not comma-grouped. "247,360" set 310px at $fs-hero — wider
  // than the other three numerals combined — and forced the whole row to a
  // width the hero could not give it. Floored rather than rounded, so the
  // figure never reads higher than the hours actually logged.
  return Math.floor(hours / 1000) + "k"
}

/**
 * Hero proof stats.
 *
 * Each of the two capability tracks the page sells now states its own volume,
 * which the previous single "50+ projects delivered" hid: 15 Odoo
 * implementations and 35 tailored builds. Those reconcile — 15 + 35 is the 50
 * that stat claimed.
 *
 * "15+ Odoo implementations" is not a new claim; the ScrollServices Odoo card
 * has published "15+ Odoo Deployments" since before this section existed.
 *
 * NOT INCLUDED, deliberately: any count of people, offices or countries. The
 * retired band's "4 Continents Served" was a scale claim and does not come back.
 */
const HERO_STATS = [
  // Every stat is exactly three lines: numeral, one label line, one note line.
  // The note is what keeps the label to a single line — anything that would
  // have wrapped is split into the qualifier instead of being allowed to run on.
  {
    value: "15+",
    label: "Odoo implementations",
    note: "Odoo Ready Partner",
  },
  {
    value: "35+",
    label: "Solutions delivered",
    note: "Tailored and AI-powered",
  },
  {
    value: developmentHours(),
    label: "Development hours",
    note: "Logged to date",
  },
  {
    // The qualifier goes on the third line, where it is part of the stat rather
    // than a footnote to it. Without "on fixed-scope work" this is a blanket
    // delivery guarantee, which the page is not allowed to make — staff
    // augmentation, retainers and delivery pods are not fixed scope. If this
    // stat ever has to shed a line, delete the stat, not the qualifier.
    value: "30",
    label: "Day post-launch warranty",
    note: "On fixed-scope work",
  },
]

const backgroundColorsArray = [
  { fontColor: "#ffffff" },
  { fontColor: "#ffffff" },
  { fontColor: "#ffffff" },
  { fontColor: "#ffffff" },
  { fontColor: "#ffffff" },
]

const getArtWorkStyles = [
  { marginTop: "18vh" },
  { transform: "scale(1.6)", marginTop: "20vh" },
  { marginTop: "15vh" },
  {},
  {},
]

const HomePageIntro = ({ data }) => {
  // The value is never read — only the setter is, to force the 6s artwork
  // re-render below. Destructured as a hole so nothing unused is bound.
  const [, setIsVisible] = useState(true)
  const [count, setCount] = useRecoilState(counterState)
  const [auditModalOpen, setAuditModalOpen] = useState(false)
  const { Title } = Typography

  useEffect(() => {
    const interval = setInterval(() => setIsVisible(false), 6000)
    const secondInterval = setInterval(() => {
      if (count < 3) {
        setCount(count + 1)
      } else {
        setCount(0)
      }
      setIsVisible(true)
    }, 6300)
    return () => {
      clearInterval(interval)
      clearInterval(secondInterval)
    }
  }, [count, setCount])

  return (
    <Animated
      className="text-and-image-container"
      style={{ position: "relative", zIndex: 0, objectFit: "cover" }}
    >
      {/* Background gradient — always fills the container */}
      <div className="hero-gradient" />

      {/* Decorative artwork — absolute, desktop only */}
      <div
        className={`art-work ${count === 1 ? "second-artwork" : ""}`}
        style={getArtWorkStyles[count]}
      />

      {/* Content — drives height on mobile */}
      <div className="animated-div">
        <Title
          style={{ color: backgroundColorsArray[count].fontColor }}
          className="title"
        >
          {HEADLINE.map(({ text, Icon }, i) => (
            <React.Fragment key={text}>
              {i > 0 && " "}
              {text}
              {/* The stop is kept, not deleted — clipped out of sight so the
                  icon reads as the punctuation, while the H1 that Google
                  indexes and a screen reader announces is still the sentence
                  in HEADLINE, unchanged. */}
              <span className="title__stop">.</span>
              <Icon />
            </React.Fragment>
          ))}
        </Title>
        <p
          style={{
            color: backgroundColorsArray[count].fontColor,
            marginBottom: "9px",
            textAlign: "center",
          }}
          className="peragraph"
        >
          {subTextArray[0]}
        </p>

        <div style={{ textAlign: "center", display: "flex", justifyContent: "center", gap: "16px", marginTop: "24px", flexWrap: "wrap" }}>
          <Link to="/contact">
            <button className="header-button-new">Book Your Free Consultation Now</button>
          </Link>
          {/* <button
            className="header-button-new"
            style={{ backgroundColor: "#2ae66f" }}
            onClick={() => setAuditModalOpen(true)}
          >
            AI Audit Your Organization for Free!
          </button> */}
        </div>

      </div>

      {/* Proof stats. Numeral leads, label beneath, and where a stat needs a
          qualifier it gets a third line — which is what lets the Odoo stat
          state both the partner status and the volume behind it.

          A SIBLING of .animated-div, not a child of it. The stats are anchored
          to the bottom of the hero, and .animated-div is position: relative, so
          absolutely positioning them inside it would anchor them to the bottom
          of the copy block instead — which is where they already were. */}
      <ul className="hero-stats">
        {HERO_STATS.map(s => (
          <li className="hero-stat" key={s.label}>
            <span className="hero-stat__value">{s.value}</span>
            <span className="hero-stat__label">{s.label}</span>
            {s.note && <span className="hero-stat__note">{s.note}</span>}
          </li>
        ))}
      </ul>
      <UserInfoModal open={auditModalOpen} onClose={() => setAuditModalOpen(false)} />
    </Animated>
  )
}

export default HomePageIntro
